import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useUser } from '../auth/AuthProvider';
import { ROLE_LABELS } from '../auth/roles';
import { Badge, Card, ErrorBanner, ListSkeleton, Meter, PageHeader } from '../components';
import { formatDate, formatUsd } from '../lib/format';

export function AccountPage() {
  const user = useUser();
  const usage = useQuery({ queryKey: ['me', 'usage'], queryFn: authApi.myUsage });

  const rows: Array<[string, ReactNode]> = [
    ['Name', user.name],
    ['Email', user.email],
    ['Role', ROLE_LABELS[user.role]],
    ['Team', user.teamName ?? '—'],
    ['Cloud speech recognition', user.cloudSttAllowed ? 'Allowed' : 'Not allowed'],
    ['Google Calendar', user.calendarConnected ? 'Connected (in the desktop app)' : 'Not connected'],
    ['Member since', formatDate(user.createdAt)],
  ];

  return (
    <div className="max-w-2xl space-y-4">
      <PageHeader title="My account" />
      <Card title="Profile">
        <dl className="divide-y divide-line">
          {rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-[12rem_1fr] gap-2 py-2 text-sm">
              <dt className="text-muted">{k}</dt>
              <dd className="text-ink">{v}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card title="AI usage this month">
        {usage.isLoading && <ListSkeleton rows={2} />}
        {usage.isError && <ErrorBanner error={usage.error} onRetry={() => void usage.refetch()} />}
        {usage.data && (
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span>
                <span className="font-medium text-ink">{formatUsd(usage.data.monthSpendUsd)}</span>{' '}
                <span className="text-muted">of {formatUsd(usage.data.limitUsd)}</span>
              </span>
              {usage.data.level === 'soft' && <Badge tone="warn">⚠ Approaching limit</Badge>}
              {usage.data.level === 'hard' && <Badge tone="danger">⚠ Limit reached</Badge>}
            </div>
            <Meter value={usage.data.monthSpendUsd} max={usage.data.limitUsd} />
          </div>
        )}
      </Card>
    </div>
  );
}
